import React, { useState, useEffect , useMemo } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Lock, Save, Loader2 } from 'lucide-react';
import { getAuthToken } from '../lib/authToken';
import { formatNom, formatPrenom } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MonProfilPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/mon-profil`, { headers });
        setProfile(res.data);
        const u = res.data.user || {};
        const f = res.data.formateur || {};
        setForm({
          civilite: f.civilite || u.civilite || '',
          nom: f.nom || u.nom || '',
          prenom: f.prenom || u.prenom || '',
          email: f.email || u.email || '',
          telephone: f.telephone || u.telephone || '',
          telephone_portable: f.telephone_portable || '',
          adresse: f.adresse || '',
          ville: f.ville || '',
          code_postal: f.code_postal || '',
        });
      } catch {
        toast.error('Erreur de chargement du profil');
      }
      setLoading(false);
    };
    fetchProfile();
  }, [headers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/mon-profil`, form, { headers });
      toast.success('Profil mis a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (pwdForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setSavingPwd(true);
    try {
      await axios.put(`${API_URL}/mon-profil/password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      }, { headers });
      toast.success('Mot de passe modifie avec succes');
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSavingPwd(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const user = profile?.user || {};
  const isFormateur = !!profile?.formateur;

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6" data-testid="mon-profil-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mon profil</h1>
          <p className="text-sm text-slate-500 mt-1">
            {user.role === 'formateur' ? 'Formateur' : user.role === 'admin' ? 'Administrateur' : user.role} — {user.email}
          </p>
        </div>

        {/* Personal info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-indigo-500" /> Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr] gap-4">
              <CiviliteSelect value={form.civilite} onChange={(v) => setForm({ ...form, civilite: v })} testId="profil-civilite" />
              <div className="space-y-1.5">
                <Label htmlFor="prenom" className="text-xs">Prénom</Label>
                <Input id="prenom" value={form.prenom} onChange={e => setForm({ ...form, prenom: formatPrenom(e.target.value) })} data-testid="input-prenom" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nom" className="text-xs">Nom</Label>
                <Input id="nom" value={form.nom} onChange={e => setForm({ ...form, nom: formatNom(e.target.value) })} data-testid="input-nom" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="telephone" className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone</Label>
                <Input id="telephone" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} data-testid="input-telephone" />
              </div>
            </div>

            {isFormateur && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="telephone_portable" className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone portable</Label>
                    <Input id="telephone_portable" value={form.telephone_portable} onChange={e => setForm({ ...form, telephone_portable: e.target.value })} data-testid="input-tel-portable" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="adresse" className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse</Label>
                  <Input id="adresse" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} data-testid="input-adresse" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="ville" className="text-xs">Ville</Label>
                    <Input id="ville" value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} data-testid="input-ville" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="code_postal" className="text-xs">Code postal</Label>
                    <Input id="code_postal" value={form.code_postal} onChange={e => setForm({ ...form, code_postal: e.target.value })} data-testid="input-cp" />
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="btn-save-profil">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Password change */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-500" /> Modifier mon mot de passe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current_password" className="text-xs">Mot de passe actuel</Label>
              <Input id="current_password" type="password" value={pwdForm.current_password} onChange={e => setPwdForm({ ...pwdForm, current_password: e.target.value })} data-testid="input-current-pwd" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="new_password" className="text-xs">Nouveau mot de passe</Label>
                <Input id="new_password" type="password" value={pwdForm.new_password} onChange={e => setPwdForm({ ...pwdForm, new_password: e.target.value })} data-testid="input-new-pwd" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm_password" className="text-xs">Confirmer le mot de passe</Label>
                <Input id="confirm_password" type="password" value={pwdForm.confirm_password} onChange={e => setPwdForm({ ...pwdForm, confirm_password: e.target.value })} data-testid="input-confirm-pwd" />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={handleChangePassword} disabled={savingPwd} variant="outline" className="gap-2" data-testid="btn-change-pwd">
                {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Changer le mot de passe
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MonProfilPage;
